import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Disc3, Trophy, RotateCcw, Search, Sparkles, UserRound } from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import UserProfileDrawer from "./UserProfileDrawer";

const MAX_LIST_RESULTS = 50;

export default function EventRaffleModal({ eventId, eventTitle, onClose }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [winnersCount, setWinnersCount] = useState(1);
  const [winners, setWinners] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [displayName, setDisplayName] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    API.get(`/votes/${eventId}/participants`)
      .then((res) => {
        const list = (res.data?.data?.participants ?? []).map((p) => p.user);
        setParticipants(list);
      })
      .catch((err) => toast.error(err.response?.data?.message || "Error cargando participantes"))
      .finally(() => setLoading(false));
    return () => clearTimeout(timeoutRef.current);
  }, [eventId]);

  // Pool restante — excluye a quienes ya salieron sorteados en esta sesión.
  const remaining = useMemo(
    () => participants.filter((p) => !winners.some((w) => w.id === p.id)),
    [participants, winners]
  );
  const n = remaining.length;
  const done = winners.length >= winnersCount;

  // Buscador — nunca renderiza la lista completa (puede haber miles de
  // participantes, por eso justamente se quitó la rueda con un gajo por
  // persona). Solo muestra resultados cuando hay texto de búsqueda.
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return participants.filter((p) => p.name?.toLowerCase().includes(q)).slice(0, MAX_LIST_RESULTS);
  }, [search, participants]);

  // "Tómbola": muestra un nombre al azar del pool, cambiando cada vez más
  // lento, hasta detenerse en el ganador real que ya decidió el servidor.
  // Nunca renderiza más de un nombre a la vez — funciona igual con 10 o con
  // 20,000 participantes, a diferencia de la rueda con un gajo por persona.
  function cycleToWinner(winner) {
    return new Promise((resolve) => {
      const pool = remaining.length > 0 ? remaining : [winner];
      const totalDuration = 2400;
      let elapsed = 0;
      let delay = 45;

      const tick = () => {
        const pick = pool[Math.floor(Math.random() * pool.length)];
        setDisplayName(pick.name);
        elapsed += delay;
        delay = Math.min(delay * 1.22, 320);
        if (elapsed < totalDuration) {
          timeoutRef.current = setTimeout(tick, delay);
        } else {
          setDisplayName(winner.name);
          setRevealed(true);
          resolve();
        }
      };
      tick();
    });
  }

  const handleSpin = async () => {
    if (spinning || n === 0 || done) return;
    setSpinning(true);
    setRevealed(false);
    try {
      const res = await API.post(`/votes/${eventId}/raffle/draw`, {
        excludeUserIds: winners.map((w) => w.id),
      });
      const winner = res.data?.data?.winner;
      await cycleToWinner(winner);
      setWinners((w) => [...w, winner]);
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo sortear");
    } finally {
      setSpinning(false);
    }
  };

  const reset = () => {
    clearTimeout(timeoutRef.current);
    setWinners([]);
    setDisplayName(null);
    setRevealed(false);
  };

  // Portal a document.body: si se renderiza anidado dentro de un elemento con
  // animación de Framer Motion (ej. la tarjeta del evento tiene whileHover),
  // ese ancestro le aplica un transform CSS, y position:fixed deja de
  // posicionarse respecto a toda la pantalla y pasa a posicionarse respecto a
  // ese ancestro — el modal se "encoge" al área de la tarjeta en vez de cubrir
  // toda la ventana. El portal evita el problema de raíz.
  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 flex items-center gap-2 truncate">
            <Disc3 size={18} className="text-supernova-500 shrink-0" />
            <span className="truncate">Sorteo — {eventTitle}</span>
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 shrink-0"><X size={18} /></button>
        </div>

        <div className="p-5">
          {loading ? (
            <p className="text-center text-gray-400 py-10">Cargando participantes...</p>
          ) : participants.length === 0 ? (
            <p className="text-center text-gray-400 py-10">Este evento no tiene votos todavía.</p>
          ) : (
            <>
              {/* Buscador de participantes — para que cualquiera pueda verificar
                  que está incluido en el sorteo, sin renderizar la lista completa */}
              <div className="mb-4">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Buscar entre ${participants.length} participantes...`}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm"
                  />
                </div>
                {search.trim() && (
                  <div className="mt-1.5 max-h-28 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-50">
                    {searchResults.length === 0 ? (
                      <p className="text-xs text-gray-400 px-3 py-2">Sin resultados</p>
                    ) : (
                      searchResults.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => setSelectedUserId(p.id)}
                          className="w-full flex items-center gap-1.5 text-xs text-gray-600 hover:bg-gray-50 px-3 py-1.5 text-left"
                        >
                          <UserRound size={11} className="text-gray-400 shrink-0" />
                          <span className="truncate">{p.name}</span>
                        </button>
                      ))
                    )}
                    {searchResults.length === MAX_LIST_RESULTS && (
                      <p className="text-[10px] text-gray-400 px-3 py-1">Mostrando los primeros {MAX_LIST_RESULTS} — sé más específico</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mb-4 text-sm">
                <span className="text-gray-500">{n} elegible(s) restante(s)</span>
                <label className="flex items-center gap-2">
                  <span className="text-gray-500">Ganadores:</span>
                  <input
                    type="number" min="1" max={participants.length}
                    value={winnersCount}
                    disabled={winners.length > 0}
                    onChange={(e) => setWinnersCount(Math.max(1, Math.min(participants.length, Number(e.target.value) || 1)))}
                    className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-center disabled:opacity-50"
                  />
                </label>
              </div>

              {/* Tómbola — un solo nombre a la vez, nunca un gajo por persona */}
              <div className={`mb-5 rounded-2xl border-2 px-4 py-8 text-center transition-colors ${
                revealed ? "border-amber-300 bg-amber-50" : "border-gray-100 bg-gray-50"
              }`}>
                {displayName ? (
                  <p className={`font-black truncate ${revealed ? "text-2xl text-amber-600" : "text-xl text-gray-700"}`}>
                    {revealed && "🎉 "}{displayName}
                  </p>
                ) : (
                  <p className="text-gray-400 flex items-center justify-center gap-2">
                    <Sparkles size={16} /> Listo para sortear
                  </p>
                )}
              </div>

              {winners.length > 0 && (
                <div className="mb-4 space-y-1.5">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Ganadores</p>
                  {winners.map((w, i) => (
                    <button
                      key={w.id}
                      onClick={() => setSelectedUserId(w.id)}
                      className="w-full flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 hover:bg-amber-100 transition text-left"
                    >
                      <Trophy size={14} className="text-amber-500 shrink-0" />
                      <span className="text-sm font-semibold text-gray-800 flex-1 truncate">#{i + 1} {w.name}</span>
                      <span className="text-[10px] text-amber-600 shrink-0">Ver perfil →</span>
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleSpin}
                  disabled={spinning || n === 0 || done}
                  className="flex-1 py-2.5 bg-supernova-500 hover:bg-supernova-600 active:scale-[0.98] text-black font-bold rounded-xl text-sm transition disabled:opacity-50"
                >
                  {spinning ? "Sorteando..." : done ? "Sorteo completo" : n === 0 ? "Sin más participantes" : "Sortear"}
                </button>
                {winners.length > 0 && (
                  <button
                    onClick={reset}
                    className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50"
                  >
                    <RotateCcw size={14} /> Reiniciar
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>

      {selectedUserId && (
        <UserProfileDrawer userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
      )}
    </div>,
    document.body
  );
}
